import React, { useState } from 'react'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, fetchSignInMethodsForEmail } from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db, googleProvider } from '../firebase/config'
import toast from 'react-hot-toast'
import SpaceTheme from '../components/SpaceTheme'

const Login = () => {
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm()

  const onSubmit = async (data) => {
    setLoading(true)
    setError('')

    try {
      // Önce e-posta adresinin hangi provider'larla kayıtlı olduğunu kontrol et
      const methods = await fetchSignInMethodsForEmail(auth, data.email)
      console.log('Sign-in methods for email:', methods)

      if (isSignUp) {
        // Kayıt ol
        if (methods.length > 0) {
          setError('Bu e-posta adresi zaten kullanımda. Google ile giriş yapmayı deneyin.')
          toast.error('Bu e-posta adresi zaten kullanımda. Google ile giriş yapmayı deneyin.')
          return
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          data.email,
          data.password
        )
        const user = userCredential.user

        // Kullanıcı bilgilerini Firestore'a kaydet
        await setDoc(doc(db, 'users', user.uid), {
          username: data.username,
          email: data.email,
          level: 'A1',
          isAdmin: false,
          profileImageUrl: '',
          createdAt: new Date()
        })

        toast.success('Hesabınız başarıyla oluşturuldu!')
      } else {
        // Giriş yap
        if (methods.includes('google.com')) {
          setError('Bu e-posta adresi Google ile kayıtlı. Google ile giriş yapın.')
          toast.error('Bu e-posta adresi Google ile kayıtlı. Google ile giriş yapın.')
          return
        }

        await signInWithEmailAndPassword(auth, data.email, data.password)
        toast.success('Başarıyla giriş yaptınız!')
      }
    } catch (error) {
      console.error('Auth error:', error)
      let errorMessage = 'Bir hata oluştu'
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı'
          break
        case 'auth/wrong-password':
          errorMessage = 'Hatalı şifre'
          break
        case 'auth/email-already-in-use':
          errorMessage = 'Bu e-posta adresi zaten kullanımda'
          break
        case 'auth/weak-password':
          errorMessage = 'Şifre en az 6 karakter olmalıdır'
          break
        case 'auth/invalid-email':
          errorMessage = 'Geçersiz e-posta adresi'
          break
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Bu e-posta adresi farklı bir yöntemle kayıtlı. Google ile giriş yapmayı deneyin.'
          break
      }
      
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    setError('')

    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Kullanıcının Firestore'da kayıtlı olup olmadığını kontrol et
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      
      if (!userDoc.exists()) {
        // Yeni kullanıcı - sadece temel bilgileri kaydet, rol seçimi sayfasına yönlendir
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          profileImageUrl: user.photoURL || '',
          createdAt: new Date(),
          lastLogin: new Date()
        })
        toast.success('Google hesabınızla başarıyla giriş yaptınız!')
        // Rol seçimi sayfasına yönlendirilecek (App.jsx'te kontrol ediliyor)
      } else {
        // Mevcut kullanıcı - son giriş zamanını güncelle
        await setDoc(doc(db, 'users', user.uid), {
          lastLogin: new Date()
        }, { merge: true })
        toast.success('Google hesabınızla başarıyla giriş yaptınız!')
      }
    } catch (error) {
      console.error('Google sign-in error:', error)
      let errorMessage = 'Google ile giriş yapılırken hata oluştu'
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Giriş işlemi iptal edildi'
          break
        case 'auth/popup-blocked':
          errorMessage = 'Popup engellendi. Lütfen popup engelleyiciyi kapatın'
          break
        case 'auth/cancelled-popup-request':
          errorMessage = 'Giriş işlemi iptal edildi'
          break
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'Bu e-posta adresi farklı bir yöntemle kayıtlı. Email/şifre ile giriş yapmayı deneyin.'
          break
      }
      
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setIsSignUp(newValue === 1)
    setError('')
    reset()
  }

  return (
    <SpaceTheme>
      <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}
        >
                     {/* Logo */}
           <Box sx={{ mb: 2, textAlign: 'center' }}>
                           <img 
                src="/logo.png" 
                alt="EngliX Logo" 
                style={{ 
                  width: 200, 
                  height: 200, 
                  objectFit: 'contain',
                  marginBottom: 20
                }}
                onError={(e) => {
                  console.log('Logo yüklenemedi, alternatif yollar deneniyor...')
                  // Alternatif logo yollarını dene
                  if (e.target.src.includes('logo.png')) {
                    e.target.src = '/logo.jpg'
                  } else if (e.target.src.includes('logo.jpg')) {
                    e.target.src = '/logo.svg'
                  } else if (e.target.src.includes('logo.svg')) {
                    e.target.src = '/logo.webp'
                  } else {
                    // Hiçbiri yüklenemezse gizle
                    e.target.style.display = 'none'
                    console.log('Logo dosyası bulunamadı')
                  }
                }}
              />
           </Box>
           
           <Typography component="h1" variant="h4" gutterBottom>
             EngliX
           </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            İnteraktif İngilizce Öğrenme Platformu
          </Typography>

          {/* Google Sign-In Button */}
          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            sx={{ 
              mb: 3,
              py: 1.5,
              borderColor: '#4285f4',
              color: '#4285f4',
              '&:hover': {
                borderColor: '#3367d6',
                backgroundColor: 'rgba(66, 133, 244, 0.04)'
              }
            }}
          >
            {googleLoading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              <Box component="span" sx={{ mr: 1, fontSize: 20 }}>G</Box>
            )}
            Google ile {isSignUp ? 'Kayıt Ol' : 'Giriş Yap'}
          </Button>

          <Divider sx={{ width: '100%', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              veya
            </Typography>
          </Divider>

          <Tabs value={isSignUp ? 1 : 0} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Giriş Yap" />
            <Tab label="Kayıt Ol" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
            {isSignUp && (
              <TextField
                margin="normal"
                required
                fullWidth
                label="Kullanıcı Adı"
                {...register('username', { required: 'Kullanıcı adı gerekli' })}
                error={!!errors.username}
                helperText={errors.username?.message}
              />
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="E-posta Adresi"
              type="email"
              {...register('email', { 
                required: 'E-posta gerekli',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Geçersiz e-posta adresi'
                }
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="Şifre"
              type="password"
              {...register('password', { 
                required: 'Şifre gerekli',
                minLength: {
                  value: 6,
                  message: 'Şifre en az 6 karakter olmalıdır'
                }
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                isSignUp ? 'Kayıt Ol' : 'Giriş Yap'
              )}
            </Button>
          </Box>
          
          {/* Privacy Policy Link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Devam ederek{' '}
              <a 
                href="/privacy-policy.html" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                Gizlilik Politikamızı
              </a>
              {' '}kabul etmiş olursunuz.
            </Typography>
          </Box>
        </Paper>
      </Box>
      </Container>
    </SpaceTheme>
  )
}

export default Login
